import { createContext, useContext, type ReactNode } from 'react'
import { useAuthzRollout } from './AuthzRolloutProvider'
import { ROLE_AUTHZ_CONTEXT, useAuthzContextQuery, type AuthzContextState } from './useAuthzContextQuery'
import { useInvalidateRecordAuthorizationOnForbidden } from './useInvalidateRecordAuthorizationOnForbidden'

const FullAuthzContext = createContext<AuthzContextState | undefined>(undefined)
const EnforcedAuthzContext = createContext<AuthzContextState | undefined>(undefined)

/**
 * Resolves what the current user has access to once, for every consumer. Mounted inside the
 * authenticated providers, so an unauthenticated route never renders it and the hooks throw there
 * rather than answering permissively.
 *
 * One element type whatever the rollout says: swapping component types here would remount the whole
 * authenticated subtree the moment LaunchDarkly answers.
 */
export const AuthzContextProvider = ({ children }: { children: ReactNode }) => {
  const rollout = useAuthzRollout()
  const full = useAuthzContextQuery(rollout)
  useInvalidateRecordAuthorizationOnForbidden()
  // Below enforcement the service answers only for the configuration surface, so to everything else
  // the organisation is still off the service — known from the rollout alone, before the service answers.
  const enforced = rollout.status === 'ready' && !rollout.fgaAuthoritative ? ROLE_AUTHZ_CONTEXT : full

  return (
    <FullAuthzContext.Provider value={full}>
      <EnforcedAuthzContext.Provider value={enforced}>{children}</EnforcedAuthzContext.Provider>
    </FullAuthzContext.Provider>
  )
}

const useRequiredContext = (context: React.Context<AuthzContextState | undefined>, message: string): AuthzContextState => {
  const value = useContext(context)
  if (value == null) {
    throw new Error(message)
  }
  return value
}

/**
 * Everything the service answered, the configuration surface included. For the route resolver, the
 * permissions-configuration UI and sensitive-data masking only: a consumer deciding access to anything
 * else reads `useAuthzContext`, which withholds the answer until the organisation enforces.
 */
export const useFullAuthzContext = (): AuthzContextState =>
  useRequiredContext(FullAuthzContext, 'useFullAuthzContext must be used within an AuthzContextProvider')

/** What decides access for the caller: the service once the organisation enforces, `role` before. */
export const useAuthzContext = (): AuthzContextState =>
  useRequiredContext(EnforcedAuthzContext, 'useAuthzContext must be used within an AuthzContextProvider')
